import { useEffect, useState } from 'react';
import { Button } from '../button/button';
import { Input } from '../input';
import { WorkspaceNameProps } from './types';
import { supabase } from '../../../lib/supabase';
import LoadingSpinner from '../loading-spinner';
import clsx from 'clsx';
import { useAuthStore } from '../../../stores/authStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../dialog';
import { Pencil } from 'lucide-react';

const WORKSPACE_NAME_MAX_LENGTH = 100;

export function WorkspaceName({ authUser }: WorkspaceNameProps) {
  const { fetchWorkspaceProfiles } = useAuthStore();

  const [name, setName] = useState(
    authUser?.currentWorkspace?.workspaces?.name || '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Update name when authUser changes
  useEffect(() => {
    setName(authUser?.currentWorkspace?.workspaces?.name || '');
  }, [authUser?.currentWorkspace?.workspaces?.name]);

  const saveName = async () => {
    setError(null);

    if (!name || name === '' || typeof name !== 'string') {
      setError('Workspace name cannot be empty.');
      return;
    }

    if (name.length > WORKSPACE_NAME_MAX_LENGTH) {
      setError(
        `Workspace name cannot exceed ${WORKSPACE_NAME_MAX_LENGTH} characters.`,
      );
      return;
    }

    if (name === authUser?.currentWorkspace?.workspaces?.name) {
      setIsOpen(false);
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('workspaces')
        .update({ name: name.trim() })
        .eq('id', authUser?.currentWorkspace?.workspace_id);

      if (error) {
        throw error;
      }

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfiles(session?.user);
        }
      });

      setIsOpen(false);
    } catch (err) {
      console.error('Error updating workspace name:', err);
      setError('Failed to update workspace name. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentName =
    authUser?.currentWorkspace?.workspaces?.name || 'Untitled Workspace';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-prompt-safecomvulcan">
          {currentName}
        </span>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-prompt-safecomfantasy"
            title="Edit workspace name"
          >
            <Pencil className="h-3.5 w-3.5 text-prompt-safecomabbey" />
          </Button>
        </DialogTrigger>
      </div>

      <DialogContent className="flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle>Edit workspace name</DialogTitle>
          <DialogDescription>
            Update the name of your workspace
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-1">
          <p className="text-sm font-medium">Workspace name:</p>
          <Input
            placeholder="My Workspace"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            disabled={isLoading}
            maxLength={WORKSPACE_NAME_MAX_LENGTH}
            className="bg-prompt-safecomwhite"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading && name.trim()) {
                saveName();
              }
            }}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            onClick={saveName}
            disabled={isLoading || !name.trim() || name === currentName}
            className={clsx(
              'hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? <LoadingSpinner strokeWidth={7} /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
